import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RotateCw } from 'lucide-react';
import { capture, captureException } from '@/lib/posthog';

import { Button } from '@/components/ui';

interface Props {
  /** Human-readable tool name, shown in the fallback copy. */
  toolName: string;
  /** Tool slug, attached to the PostHog report for breakdowns. */
  toolSlug: string;
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Wraps a client-side tool runner so a render/commit crash degrades to a
 * recovery card instead of white-screening the whole SPA.
 *
 * Motivating bug (2026-05-26, recurred 2026-07-09): /tools/merge threw
 * DOMExceptions ("Failed to execute 'removeChild'/'insertBefore' on 'Node'")
 * mid-commit when a translating browser extension split React's text nodes and
 * the file list then mutated. `translate="no"` on merge.tsx and app-wide
 * reconciler guards in `lib/browser-compat.ts` now prevent that class at the
 * source (translators honor the opt-out inconsistently, which is why it
 * recurred). This boundary stays as the last line of defense — it contains any
 * residual render/commit crash and keeps reporting it to PostHog.
 */
export class ToolErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidUpdate(prevProps: Props) {
    // React Router reuses this instance across /tools/:slug. Clear the caught
    // error when the tool changes so a crash on one tool doesn't leave the
    // recovery card stuck on the next (healthy) tool.
    if (this.state.hasError && prevProps.toolSlug !== this.props.toolSlug) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    captureException(error, {
      tool_slug: this.props.toolSlug,
      component_stack: info.componentStack,
      source: 'tool_error_boundary',
    });
    capture('error_boundary_triggered', {
      tool_slug: this.props.toolSlug,
      tool_name: this.props.toolName,
      message: error.message,
    });
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        role="alert"
        className="rounded-xl border border-destructive/40 bg-destructive/10 p-8 text-center"
      >
        <AlertTriangle className="mx-auto h-8 w-8 text-destructive" />
        <h2 className="mt-3 text-lg font-semibold text-foreground">Something went wrong</h2>
        <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
          The {this.props.toolName} tool hit an unexpected error. This can happen when a browser
          extension (like a page translator) interferes with the page. Reloading usually fixes it.
        </p>
        <Button
          variant="unstyled"
          onClick={() => window.location.reload()}
          className="mt-4 inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
        >
          <RotateCw className="h-4 w-4" /> Reload page
        </Button>
      </div>
    );
  }
}
